import React, { createContext, useState, useContext, useCallback } from 'react';

import IClient from '~/interfaces/IClient';
import { useAuth } from './auth';

import api from '~/services/api';

interface ClientsContextData {
  clients: IClient[];
  selectedClient: IClient;
  isInitialized: boolean;
  newClient: IClient | null;
  setNewClient: (client: IClient | null) => void;
  setSelectedClient(value: IClient): void;
  loadClients(): Promise<void>;
  addClient: (client: IClient) => Promise<void>;
  updateClient: (client: IClient) => Promise<void>;
  removeClient: (id: string) => Promise<void>;
}

const ClientsContext = createContext<ClientsContextData>(
  {} as ClientsContextData,
);

const { REACT_APP_FIREBASE_PROJECT } = process.env;

export const ClientsProvider: React.FC = ({ children }) => {
  const { company } = useAuth();
  const [clients, setClients] = useState<IClient[]>([]);
  const [selectedClient, setSelectedClient] = useState({} as IClient);
  const [isInitialized, setIsInitialized] = useState(false);
  const [newClient, setNewClient] = useState<IClient | null>(null);

  const loadClients = useCallback(async () => {
    if (company.uid) {
      const response = await api.get(`/clients/${REACT_APP_FIREBASE_PROJECT}`);
      const parseJson = JSON.parse(
        Buffer.from(response.data.result, 'base64').toString(),
      );
      setClients(parseJson);
      setIsInitialized(true);
    }
  }, [company]);

  const addClient = useCallback(
    async (client: IClient) => {
      setNewClient(client);
      await api.post(`clients/${REACT_APP_FIREBASE_PROJECT}`, client);
      await api.delete(`/clients/${REACT_APP_FIREBASE_PROJECT}`);
      await loadClients();
    },
    [loadClients],
  );

  const removeClient = useCallback(
    async (id: string) => {
      await api.delete(`clients/${REACT_APP_FIREBASE_PROJECT}/${id}`);
      await api.delete(`/clients/${REACT_APP_FIREBASE_PROJECT}`);
      await loadClients();
    },
    [loadClients],
  );

  const updateClient = useCallback(
    async (client: IClient) => {
      await api.put(`clients/${REACT_APP_FIREBASE_PROJECT}`, client);
      await api.delete(`/clients/${REACT_APP_FIREBASE_PROJECT}`);
      await loadClients();
    },
    [loadClients],
  );

  return (
    <ClientsContext.Provider
      value={{
        clients,
        newClient,
        loadClients,
        selectedClient,
        setSelectedClient,
        isInitialized,
        addClient,
        updateClient,
        removeClient,
        setNewClient,
      }}
    >
      {children}
    </ClientsContext.Provider>
  );
};

export function useClients(): ClientsContextData {
  const context = useContext(ClientsContext);
  return context;
}
